/**
 * Marketing attribution for the trial sign-up. The website puts its tracking parameters on the
 * sign-up link (utm_*, gclid, lqid, abtest, page, platform, and anything else it wants
 * reported) and the sign-up passes them on to HubSpot and Salesforce: the whole query as one
 * parameter string (`spid_trial_src_ref__c`) plus one hidden input per known parameter. The
 * values are the ones marketing's SAP Tracking script (sap-example.js) computes, so its
 * console check on the sign-up page sees the same fields.
 *
 * The parameters are remembered for the session on whichever public page first sees them, so
 * a visitor who lands on the home page and then clicks "Start free trial" is still attributed.
 * A later link's parameters override the remembered ones key by key ("latest_*").
 */
import type { HubSpotTrialForm } from '../services/api'
import { hubspotTrackingCookie } from './marketingAnalytics'

const STORAGE_KEY = 'trialQuery'
const MAX_SOURCE_REF_LENGTH = 2000
const MAX_VALUE_LENGTH = 255

/** Legacy parameter names the website may still send, and the utm_* name each stands for. */
export const WEB_PARAMS: Record<string, string> = {
  web_source: 'utm_source',
  web_medium: 'utm_medium',
  web_term: 'utm_term',
  web_device: 'utm_device',
  web_content: 'utm_content',
  web_campaign: 'utm_campaign',
}

/** Ad click identifiers: placed last in the parameter string, in the order they came. */
const TRAILING_PARAMS: ReadonlySet<string> = new Set(['gclid', 'msclkid', 'lqid'])

/**
 * The trial form's hidden inputs, in the order marketing's script sets them. Every one is
 * rendered on the sign-up page, empty or not, so the script finds each of them.
 */
export const HIDDEN_INPUT_NAMES = [
  'spid_trial_src_ref__c',
  'pageName',
  'pageUri',
  'hutk',
  'region',
  'marketing-optin',
  'latest_form_page',
  'platform',
  'gclid',
  'lqid',
  'latest_utm_term',
  'latest_utm_medium',
  'latest_utm_device',
  'latest_utm_content',
  'latest_utm_source',
  'latest_utm_campaign',
  'latest_marketing_page',
  'marketing_page__c',
  'latest_abtest',
] as const

export type TrialTrackingFields = Omit<HubSpotTrialForm, 'marketing_consent_check' | 'region'> & {
  spid_trial_src_ref__c?: string
}

function stored(): URLSearchParams {
  try {
    return new URLSearchParams(window.sessionStorage.getItem(STORAGE_KEY) || '')
  } catch {
    return new URLSearchParams()
  }
}

/** `over` on top of `base`: a key present in `over` replaces every value of it in `base`. */
function merge(base: URLSearchParams, over: URLSearchParams): URLSearchParams {
  const merged = new URLSearchParams(base)
  for (const key of new Set(over.keys())) {
    merged.delete(key)
    for (const value of over.getAll(key)) merged.append(key, value)
  }
  return merged
}

/** Keep this page's query for the session, on top of whatever earlier pages carried. */
export function rememberTrialQuery(search: string): void {
  const current = new URLSearchParams(search)
  if ([...current.keys()].length === 0) return
  try {
    window.sessionStorage.setItem(STORAGE_KEY, merge(stored(), current).toString())
  } catch {
    // Storage unavailable (private mode, blocked): the query still serves this page.
  }
}

/** The tracking parameters in force: the session's, with this page's query on top. */
export function trialQuery(search: string): URLSearchParams {
  return merge(stored(), new URLSearchParams(search))
}

/**
 * The `spid_trial_src_ref__c` value, as marketing's script builds it: every parameter of the
 * link as one decoded query string ("utm_campaign=Spring Sale"). A legacy web_* parameter is
 * renamed to its utm_* twin (appended last) or dropped when the twin is already present, and
 * the click identifiers go to the end in the order they came.
 */
export function adjustParamString(source: URLSearchParams): string {
  const params = new URLSearchParams(source)
  for (const [webKey, utmKey] of Object.entries(WEB_PARAMS)) {
    const legacy = params.get(webKey)
    if (legacy === null) continue
    if (!params.has(utmKey)) params.append(utmKey, legacy)
    params.delete(webKey)
  }
  const entries = [...params.entries()]
  const ordered = [
    ...entries.filter(([key]) => !TRAILING_PARAMS.has(key)),
    ...entries.filter(([key]) => TRAILING_PARAMS.has(key)),
  ]
  return ordered.map(([key, value]) => `${key}=${value}`).join('&').slice(0, MAX_SOURCE_REF_LENGTH)
}

function value(params: URLSearchParams, key: string): string | undefined {
  const raw = params.get(key)?.trim()
  return raw ? raw.slice(0, MAX_VALUE_LENGTH) : undefined
}

/** ISO 639-1 language of the browser ("en-US" -> "en"): the language for the trial nurture emails. */
function language(): string | undefined {
  const code = (navigator.language || '').slice(0, 2).toLowerCase()
  return /^[a-z]{2}$/.test(code) ? code : undefined
}

/** The trial form's hidden inputs for this page (the router's location), from the parameters in force. */
export function trialTrackingFields({ pathname, search }: { pathname: string; search: string }): TrialTrackingFields {
  const params = trialQuery(search)
  const utm = (name: string) => value(params, `utm_${name}`) ?? value(params, `web_${name}`)
  const marketingPage = value(params, 'page')
  const fields: TrialTrackingFields = {
    spid_trial_src_ref__c: adjustParamString(params) || undefined,
    latest_utm_source: utm('source'),
    latest_utm_medium: utm('medium'),
    latest_utm_term: utm('term'),
    latest_utm_device: utm('device'),
    latest_utm_content: utm('content'),
    latest_utm_campaign: utm('campaign'),
    latest_abtest: value(params, 'abtest'),
    latest_form_page: pathname,
    latest_marketing_page: marketingPage,
    marketing_page__c: marketingPage,
    gclid: value(params, 'gclid'),
    lqid: value(params, 'lqid'),
    platform: value(params, 'platform') ?? 'sap',
    locale__c: language(),
    hutk: hubspotTrackingCookie(),
    pageName: document.title,
    pageUri: window.location.hostname + pathname,
  }
  return Object.fromEntries(Object.entries(fields).filter(([, v]) => v !== undefined)) as TrialTrackingFields
}
